import { FormInput } from "@general-backoffice/core/index";
import type { Strategy } from "@investhinks/api-client";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import { CURRENT_GEN_STRATEGY, EARLY_ACCESS_STRATEGY } from "../../config/v5fxEarlyAccess";
import { V6FX_STRATEGY } from "../../config/v6fxEarlyAccess";
import { risks } from "../../utils/risks";
import useStrategies from "./hooks/useStrategies";

interface StrategyOption {
  name: string;
  strategy: Strategy;
  disabled?: boolean;
  risk: string;
  color?: string;
  proOnly?: boolean;
  comingSoon?: boolean;
}

interface RiskStrategySelectionInputProps {
  strategy?: Strategy | null;
  risk?: string | null;
  onChange: (strategy: Strategy | null, risk: string | null) => void;
  exchangeType?: string;
  required?: boolean;
  isLoading?: boolean;
  v4Only?: boolean;
  v5fxEarlyAccess?: boolean;
  v6fxEarlyAccess?: boolean;
  label?: React.ReactNode;
  isDisabled?: boolean;
  [key: string]: unknown;
}

const RiskStrategySelectionInput = ({
  strategy,
  risk,
  onChange,
  exchangeType,
  required,
  isLoading: isLoadingParent,
  v4Only,
  v5fxEarlyAccess,
  v6fxEarlyAccess,
  ...props
}: RiskStrategySelectionInputProps) => {
  const { t } = useTranslation("utils", { keyPrefix: "risks" });

  const { strategies, isLoading } = useStrategies({
    filters: exchangeType ? { exchangeType: { $eq: exchangeType } } : undefined,
  });

  const options = useMemo(() => {
    let strats = strategies;
    // For new bots, restrict to the current generation (V4FX) plus the early-access
    // next generation (V5FX). V5FX is shown to everyone but only selectable for
    // allowlisted users; others see it disabled as "Coming soon" (handled below).
    if (v4Only)
      strats = strategies?.filter(
        (s) => s?.name === CURRENT_GEN_STRATEGY || s?.name === EARLY_ACCESS_STRATEGY || s?.name === V6FX_STRATEGY
      );

    const risksRecord: Record<string, { color?: string; proOnly?: boolean; hideSmall?: boolean } | undefined> = risks;
    const strategyOptions = (strats || []).reduce<StrategyOption[]>((r, strategy) => {
      const riskOptions = Array.isArray(strategy?.configOptions?.riskOptions) ? strategy.configOptions.riskOptions : [];
      // V5FX is gated behind the early-access allowlist: visible to all, selectable only when allowed.
      const comingSoon = (strategy?.name === EARLY_ACCESS_STRATEGY && !v5fxEarlyAccess) ||
        (strategy?.name === V6FX_STRATEGY && (!v6fxEarlyAccess || strategy.configOptions?.releaseAvailable !== true));
      for (const opt of riskOptions) {
        if (!opt || typeof opt !== "object") continue;
        const { name, value, disabled } = opt;
        const riskConfig = risksRecord[value];
        const { color, proOnly } = riskConfig || {};
        r.push({
          name: name || "",
          strategy: { ...strategy },
          disabled: disabled || comingSoon,
          comingSoon,
          risk: value,
          color,
          proOnly,
        });
      }
      return r;
    }, []);

    // Sort options by risk following risks key order (reversed: higher risk first)
    const riskKeys = Object.keys(risks).reverse();
    const sortedOptions = riskKeys.reduce<StrategyOption[]>((r, risk) => {
      const options = strategyOptions.filter((o) => o.risk === risk);
      if (options.length) r.push(...options);
      return r;
    }, []);

    // Within each risk group, sort the current generation (V4FX) first
    sortedOptions.sort((a, b) => {
      if (a.risk !== b.risk) return 0;
      if (a.strategy.name === CURRENT_GEN_STRATEGY && b.strategy.name !== CURRENT_GEN_STRATEGY) return -1;
      if (b.strategy.name === CURRENT_GEN_STRATEGY && a.strategy.name !== CURRENT_GEN_STRATEGY) return 1;
      return 0;
    });

    return sortedOptions;
  }, [strategies, v4Only, v5fxEarlyAccess, v6fxEarlyAccess]);

  const currOption = useMemo(() => {
    if (options?.length && risk && strategy) {
      const i = options.findIndex((o) => o?.risk === risk && o?.strategy?.id === (strategy?.id || strategy));
      if (i > -1) return { ...options[i], index: i };
    }
    return null;
  }, [options, risk, strategy]);

  // Filter out high risk options
  const visibleOptions = useMemo(() => options.filter((o) => o.risk !== "high"), [options]);

  // Group by generation: V4FX (current), V5FX (early access / coming soon),
  // anything else (previous gen, e.g. existing-bot strategies), Custom Risk at bottom.
  const groupedOptions = useMemo(
    () =>
      [
        {
          label: t("groupCurrentGen"),
          options: visibleOptions.filter(
            (o) => o.strategy.name === CURRENT_GEN_STRATEGY && o.risk !== "custom"
          ),
        },
        {
          label: t(v5fxEarlyAccess ? "groupV5fxEarly" : "groupV5fxComingSoon"),
          options: visibleOptions.filter(
            (o) => o.strategy.name === EARLY_ACCESS_STRATEGY && o.risk !== "custom"
          ),
        },
        {
          label: t(v6fxEarlyAccess ? "groupV6fxEarly" : "groupV6fxComingSoon"),
          options: visibleOptions.filter((o) => o.strategy.name === V6FX_STRATEGY && o.risk !== "custom"),
        },
        {
          label: t("groupPreviousGen"),
          options: visibleOptions.filter(
            (o) =>
              o.strategy.name !== CURRENT_GEN_STRATEGY &&
              o.strategy.name !== EARLY_ACCESS_STRATEGY &&
              o.strategy.name !== V6FX_STRATEGY &&
              o.risk !== "custom"
          ),
        },
        {
          label: t("groupCustomRisk"),
          options: visibleOptions.filter((o) => o.risk === "custom"),
        },
      ].filter((g) => g.options.length > 0),
    [visibleOptions, t, v5fxEarlyAccess, v6fxEarlyAccess]
  );

  const formatRiskOptionLabel = (
    option: StrategyOption,
    { context }: { context: "menu" | "value" }
  ) => (
    <div className="d-flex align-items-center justify-content-between">
      <div>
        <span className="font-weight-500 mr-2">{option.name}</span>
        {option.proOnly && <ProOnlyBadge compact={context === "value"} />}
        {option.comingSoon && <ComingSoonBadge compact={context === "value"} />}
      </div>
      {context === "menu" && (
        <Badge
          color={option.color}
          className="border rounded-pill text-capitalize font-weight-400 py-1 ml-2"
        >
          {t("risk", { risk: t(option.risk) })}
        </Badge>
      )}
    </div>
  );

  const getOptionValue = (o: StrategyOption) => `${o.strategy?.id}-${o.risk}`;

  const areLoading = isLoading || isLoadingParent;

  return (
    <div style={{ position: "relative" }}>
      <FormInput.Select
        {...props}
        onChange={(option: StrategyOption | null) => {
          if (option) onChange(option.strategy, option.risk);
          else onChange(null, null);
        }}
        options={groupedOptions}
        formatOptionLabel={formatRiskOptionLabel}
        getOptionValue={getOptionValue}
        value={currOption}
        isOptionDisabled={(o: StrategyOption) => !!o.disabled}
        isDisabled={areLoading || props.isDisabled}
        isClearable={false}
        isSearchable={false}
      />
      {/* Hidden input for HTML5 required validation */}
      <input
        style={{ position: "absolute", bottom: 0, left: 0, height: 0, width: 0, opacity: 0, pointerEvents: "none" }}
        name={typeof props?.label === "string" ? props.label : undefined}
        value={currOption?.risk ?? ""}
        required={required}
        readOnly
        tabIndex={-1}
      />
    </div>
  );
};

export default RiskStrategySelectionInput;

export const ProOnlyBadge = ({ compact = false }) => {
  const { t } = useTranslation("utils", { keyPrefix: "risks" });
  return (
    <Badge color="warning" className="rounded-pill text-capitalize h-auto" style={{ fontSize: ".5rem" }}>
      <i className={`fa fa-crown${compact ? "" : " mr-1"}`} />
      {!compact && <span className="font-weight-400">{t("proEarlyAccess")}</span>}
    </Badge>
  );
};

export const ComingSoonBadge = ({ compact = false }) => {
  const { t } = useTranslation("utils", { keyPrefix: "risks" });
  return (
    <Badge color="secondary" className="rounded-pill text-capitalize h-auto" style={{ fontSize: ".5rem" }}>
      <i className={`fa fa-clock${compact ? "" : " mr-1"}`} />
      {!compact && <span className="font-weight-400">{t("comingSoon")}</span>}
    </Badge>
  );
};
